import React from "react";
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Chip,
  alpha,
} from "@mui/material";
import {
  HeadphonesOutlined,
  ImageOutlined,
  PlayCircleOutlineOutlined,
  CampaignOutlined,
  Face2Outlined,
  LibraryMusicOutlined,
} from "@mui/icons-material";
import { useCustomTheme } from "../../utils/useCustomTheme";
import { TranslateContext } from "../../context/TranslateContext";
import { parseHomepageShowcase, showcaseHasItems } from "../../utils/homepageShowcase";
import { isShowcaseImageUrl, isShowcaseVideoUrl } from "../../utils/showcaseMedia";
import ShowcaseMusicArt from "./ShowcaseMusicArt";
import ShowcasePreviewModal from "./ShowcasePreviewModal";

const TAB_META = {
  product_ads: { icon: CampaignOutlined, labelKey: "homeShowcaseAds", fallback: "Product ads" },
  characters: { icon: Face2Outlined, labelKey: "homeShowcaseCharacters", fallback: "Characters" },
  music: { icon: LibraryMusicOutlined, labelKey: "homeShowcaseMusic", fallback: "Music" },
};

function previewKind(item, category) {
  if (item.type === "video" || isShowcaseVideoUrl(item.media_url)) return "video";
  if (item.type === "audio" || item.audio_url || category === "music") return "audio";
  return "image";
}

function previewChipLabel(kind, lang) {
  if (kind === "video") return lang?.homeShowcasePlay || "Play";
  if (kind === "audio") return lang?.homeShowcaseListen || "Listen";
  return lang?.homeShowcaseView || "View";
}

function previewChipIcon(kind) {
  if (kind === "video") return <PlayCircleOutlineOutlined sx={{ fontSize: "0.85rem !important" }} />;
  if (kind === "audio") return <HeadphonesOutlined sx={{ fontSize: "0.85rem !important" }} />;
  return <ImageOutlined sx={{ fontSize: "0.85rem !important" }} />;
}

function mediaFrameSx(kind, accent) {
  if (kind === "video") {
    return {
      position: "relative",
      width: "100%",
      aspectRatio: "9 / 16",
      bgcolor: alpha(accent, 0.06),
      overflow: "hidden",
    };
  }
  return {
    position: "relative",
    width: "100%",
    aspectRatio: "16 / 9",
    bgcolor: alpha(accent, 0.06),
    overflow: "hidden",
  };
}

function gridColsForTab(tab) {
  if (tab === "product_ads") {
    return { xs: 6, sm: 4, md: 3, lg: 2 };
  }
  return { xs: 12, sm: 6, md: 4 };
}

function ShowcaseCard({ item, category, accent, onOpen }) {
  const { lang } = React.useContext(TranslateContext);
  const kind = previewKind(item, category);
  const isVideo = kind === "video";
  const isAudio = kind === "audio";
  const showImage = !isVideo && !isAudio && isShowcaseImageUrl(item.media_url);

  const inner = (
    <>
      <Box sx={mediaFrameSx(kind, accent)}>
        {isVideo && item.media_url ? (
          <Box
            component="video"
            src={item.media_url}
            muted
            loop
            playsInline
            autoPlay
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              pointerEvents: "none",
            }}
          />
        ) : isAudio ? (
          <ShowcaseMusicArt title={item.title} accent={accent} compact />
        ) : showImage ? (
          <CardMedia
            component="img"
            image={item.media_url}
            alt={item.title}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(145deg, ${alpha(accent, 0.15)} 0%, ${alpha(accent, 0.04)} 100%)`,
            }}
          >
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {item.title}
            </Typography>
          </Box>
        )}
        <Chip
          size="small"
          icon={previewChipIcon(kind)}
          label={previewChipLabel(kind, lang)}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "rgba(0,0,0,0.55)",
            color: "#fff",
            fontWeight: 700,
          }}
        />
      </Box>
      <CardContent sx={{ py: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap>
          {item.title}
        </Typography>
      </CardContent>
    </>
  );

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        overflow: "hidden",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": { transform: "translateY(-4px)", boxShadow: 6 },
      }}
    >
      <CardActionArea
        onClick={() => onOpen({ ...item, category })}
        sx={{ display: "block", textAlign: "left" }}
      >
        {inner}
      </CardActionArea>
    </Card>
  );
}

export default function ShowcaseSection({ web }) {
  const { lang } = React.useContext(TranslateContext);
  const { config, isDark } = useCustomTheme();
  const accent = isDark ? config.primary_dark || config.primary : config.primary;

  const showcase = React.useMemo(
    () => parseHomepageShowcase(web?.homepage_showcase_json),
    [web?.homepage_showcase_json],
  );

  const [tab, setTab] = React.useState("product_ads");
  const [previewItem, setPreviewItem] = React.useState(null);

  React.useEffect(() => {
    const order = ["product_ads", "characters", "music"];
    const first = order.find((k) => showcase[k]?.length > 0);
    if (first) setTab(first);
  }, [showcase]);

  if (!showcaseHasItems(showcase)) return null;

  const items = showcase[tab] || [];

  return (
    <Box
      component="section"
      id="showcase"
      sx={{
        py: { xs: 5, md: 7 },
        pb: { xs: 6, md: 8 },
        overflow: "visible",
        background: isDark
          ? `linear-gradient(180deg, ${alpha(accent, 0.08)} 0%, ${config.background_default_dark} 40%)`
          : `linear-gradient(180deg, ${alpha(accent, 0.06)} 0%, ${config.background_default_light} 50%)`,
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={1.5} textAlign="center" mb={4} maxWidth={680} mx="auto">
          <Typography variant="overline" fontWeight={700} letterSpacing="0.12em" color="text.secondary">
            {lang?.homeShowcaseBadge || "Made with Beyund AI"}
          </Typography>
          <Typography
            component="h2"
            variant="h3"
            fontWeight={800}
            letterSpacing="-0.03em"
            sx={{ fontFamily: config.font_family }}
          >
            {lang?.homeShowcaseTitle || "See what creators"}{" "}
            <Box component="span" sx={{ color: accent }}>
              {lang?.homeShowcaseTitleAccent || "are shipping"}
            </Box>
          </Typography>
          <Typography variant="body1" color="text.secondary" lineHeight={1.7}>
            {lang?.homeShowcaseSubtitle ||
              "Clips, story videos, product ads, and characters from real creators — tap any example to preview."}
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={1} mb={4}>
          {Object.keys(TAB_META).map((key) => {
            if (!showcase[key]?.length) return null;
            const meta = TAB_META[key];
            const Icon = meta.icon;
            const active = tab === key;
            return (
              <Chip
                key={key}
                icon={<Icon />}
                label={lang?.[meta.labelKey] || meta.fallback}
                onClick={() => setTab(key)}
                variant={active ? "filled" : "outlined"}
                color={active ? "primary" : "default"}
                sx={{ fontWeight: 700, px: 0.5 }}
              />
            );
          })}
        </Stack>

        <Grid container spacing={2.5} alignItems="flex-start">
          {items.map((item, idx) => {
            const cols = gridColsForTab(tab);
            return (
              <Grid item {...cols} key={`${tab}-${idx}-${item.title}`}>
                <ShowcaseCard
                  item={item}
                  category={tab}
                  accent={accent}
                  onOpen={setPreviewItem}
                />
              </Grid>
            );
          })}
        </Grid>
      </Container>

      <ShowcasePreviewModal
        open={Boolean(previewItem)}
        item={previewItem}
        onClose={() => setPreviewItem(null)}
        accent={accent}
      />
    </Box>
  );
}
